/**
 * 燃料費一覧 API（/api/fuel-expenses）。F-EXP-01 / S-50
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { expenseKeys } from '../common/api';

export type Rounding = '四捨五入' | '切り捨て';

export interface FuelRowDto {
  voyageReportId: number;
  vesselId: number;
  vesselNo: number;
  vesselName: string;
  vesselNameKana: string;
  registrationNo: string;
  businessOperatorId: number;
  periodFrom: string;
  periodTo: string;
  periodDays: number;
  fuelDays: number | null;
  dailyAverage: number;
  fuelDate: string | null;
  fuelType: string | null;
  fuelTaxRounding: Rounding;
  fuelQuantity: number | null;
  fuelUnitPrice: number | null;
  fuelLightOilTax: number;
  fuelAmountExclTax: number;
  fuelTax: number;
  fuelTotal: number;
  fuelNote: string | null;
  fuelSubsidyScheduledOn: string | null;
  fuelSubsidyPaidOn: string | null;
  lubeUnitPrice: number | null;
  lubeAmountExclTax: number;
  lubeTotal: number;
  lubeSubsidyScheduledOn: string | null;
  lubeSubsidyPaidOn: string | null;
  devDate: string | null;
  devTaxRounding: Rounding;
  devAmountExclTax: number;
  devTax: number;
  devTotal: number;
  devSubsidyScheduledOn: string | null;
  devSubsidyPaidOn: string | null;
}

export interface FuelListDto {
  items: FuelRowDto[];
  total: number;
  totals: Record<string, number>;
  operatorHasTax: boolean;
}

export interface FuelFilter {
  businessOperatorId?: number | null;
  vesselName?: string;
  surveyFrom?: string;
  surveyTo?: string;
  scheduledFrom?: string;
  scheduledTo?: string;
  paidFrom?: string;
  paidTo?: string;
  fuelPaid?: '支払済' | '未支払' | '両方';
  sort?: '参加船名簿の順' | '調査日の順';
}

/** PUT /api/fuel-expenses の 1 行 */
export type FuelSaveItem = Pick<
  FuelRowDto,
  | 'voyageReportId'
  | 'fuelDate'
  | 'fuelTaxRounding'
  | 'fuelQuantity'
  | 'fuelAmountExclTax'
  | 'fuelTax'
  | 'fuelTotal'
  | 'fuelNote'
  | 'fuelSubsidyScheduledOn'
  | 'fuelSubsidyPaidOn'
  | 'lubeUnitPrice'
  | 'lubeAmountExclTax'
  | 'lubeSubsidyScheduledOn'
  | 'lubeSubsidyPaidOn'
  | 'devDate'
  | 'devTaxRounding'
  | 'devAmountExclTax'
  | 'devTax'
  | 'devTotal'
  | 'devSubsidyScheduledOn'
  | 'devSubsidyPaidOn'
>;

export interface FuelWarning {
  voyageReportId: number;
  rowIndex: number;
  code: string;
  message: string;
}

export const fuelKeys = {
  all: [...expenseKeys.all, 'fuel'] as const,
  list: (year: number, f: FuelFilter) => [...fuelKeys.all, 'list', year, f] as const,
};

export function useFuelExpenses(year: number, filter: FuelFilter, enabled: boolean) {
  return useQuery({
    queryKey: fuelKeys.list(year, filter),
    queryFn: ({ signal }) => api.get<FuelListDto>('/fuel-expenses', { fiscalYear: year, ...filter }, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePreviewFuel() {
  return useMutation({
    mutationFn: (items: FuelSaveItem[]) =>
      api.post<{ errors: FuelWarning[]; warnings: FuelWarning[]; confirmations: string[] }>('/fuel-expenses/preview', { items }),
  });
}

export function useSaveFuel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { items: FuelSaveItem[]; confirmations: Record<string, boolean> }) => api.put<{ updated: number }>('/fuel-expenses', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: fuelKeys.all }),
  });
}

export type FuelCalcInput =
  | { kind: 'fuel'; voyageReportId: number; fuelDate: string | null; total: number; taxAmount: number | null; taxRounding: Rounding }
  | { kind: 'lubricant'; voyageReportId: number; fuelQuantity: number | null; unitPrice: number | null }
  | { kind: 'development'; voyageReportId: number; devDate: string | null; total: number; taxAmount: number | null; taxRounding: Rounding };

export function useFuelCalc() {
  return useMutation({
    mutationFn: (input: FuelCalcInput) =>
      api.post<{ amountExclTax: number; tax: number; total: number; taxRate?: number; coefficient?: number }>('/fuel-expenses/calc', input),
  });
}
